import * as React from "react";
import IconClose from "../Icons/IconClose";
import ShopifyProduct from "@app/interface/ShopifyProduct";
import { formatMoney } from "@shopify/theme-currency";
import { ProductStockDrawerStore } from "./ProductStockDrawerStore";

type Props = {
  product: ShopifyProduct;
  moneyFormat: string;
  open: boolean;
  onClose?: () => void;
  onOpen?: () => void;
  search: { value: string; error: string };
  setSearch: (search: { value: string; error: string }) => void;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  savedLocation?: boolean;
  setSavedLocation: (savedLocation: boolean) => void;
  selectedVariant?: string;
  setSelectedVariant: (selectedVariant: string) => void;
  isLoading: boolean;
  error?: string;
  storesWithinRange?: any[];
  handleSelectedStore?: (storeId: string) => void;
  selectedStore: string;
};

const FindInStoreDrawer: React.FC<Props> = ({
  product,
  moneyFormat,
  open,
  onClose,
  search,
  setSearch,
  handleSubmit,
  savedLocation,
  setSavedLocation,
  selectedVariant,
  setSelectedVariant,
  isLoading,
  error,
  storesWithinRange,
  handleSelectedStore,
  selectedStore,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    if (open) {
      inputRef.current?.focus();
    }
  }, [open]);

  // listen for the ESC key and close on press
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  const disableScroll = () => {
    const bodyElem = document.querySelector("body");
    if (bodyElem) {
      bodyElem.classList.add("body__scroll-locked");
    }
  };

  const enableScroll = () => {
    const bodyElem = document.querySelector("body");
    if (bodyElem) {
      bodyElem.classList.remove("body__scroll-locked");
    }
  };

  React.useEffect(() => {
    if (open) {
      disableScroll();
    } else {
      enableScroll();
    }
  }, [open]);

  return (
    <div className={`find-in-store ${open ? "find-in-store--open" : ""}`}>
      <div className="find-in-store__overlay" onClick={onClose}></div>
      <div className="find-in-store__drawer">
        <header className="find-in-store__drawer--header">
          <span className="find-in-store__heading">Find in Store</span>
          <button className="find-in-store__close" onClick={onClose}>
            <IconClose />
          </button>
        </header>

        <div className="find-in-store__drawer--body">
          <div className="find-in-store__drawer--content">
            <form onSubmit={handleSubmit} action="" method="POST">
              <input
                ref={inputRef}
                className="find-in-store__drawer--input"
                type="text"
                placeholder="Postcode, Suburb"
                value={search.value}
                onChange={(e) =>
                  setSearch({
                    ...search,
                    value: e.target.value,
                  })
                }
              />
              {search.error && <p className="find-in-store__drawer--error">{search.error}</p>}
              {error && <p className="find-in-store__drawer--error">{error}</p>}
            </form>

            {/* <div className="find-in-store__drawer--save">
              <input
                type="checkbox"
                id="saveForLater"
                className="find-in-store__drawer--checkbox input-checkbox"
                checked={!!savedLocation}
                onChange={() => setSavedLocation(!savedLocation)}
                // value={savedLocation ? "true" : "false"}
              />
              <label htmlFor="saveForLater">Save my location for later</label>
            </div> */}

            <div className="find-in-store__drawer--product">
              <div className="find-in-store__drawer--product-image">
                <figure>
                  <img src={product.featured_image} alt={product.title} />
                </figure>
              </div>

              <div className="find-in-store__drawer--product-details">
                <div className="find-in-store__drawer--product-info">
                  <h2 className="find-in-store__drawer--product-title">{product.title}</h2>
                  <p>{formatMoney(product.price, moneyFormat).replace(".00", "")}</p>
                </div>

                {/* {product.variants.length > 1 && (
                  <div className="find-in-store__drawer--product-variants">
                    {product.variants.map((variant) => {
                      const isSelected = selectedVariant === variant.sku.toString();
                      return (
                        <div
                          key={variant.id}
                          className={`find-in-store__drawer--product-variant ${variant.available ? "available" : "out-of-stock"} ${isSelected ? "selected" : ""}`}
                        >
                          <input type="radio" name={variant.title} id={variant.id.toString()} />
                          <label
                            onClick={() => {
                              setSelectedVariant(variant.sku.toString());
                            }}
                            htmlFor={variant.id.toString()}
                          >
                            {variant.title}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                )} */}
              </div>
            </div>

            <>
              {isLoading ? (
                <p className="find-in-store__drawer--loading">Loading ...</p>
              ) : storesWithinRange?.length > 0 && !error ? (
                <div className="find-in-store__drawer--stores">
                  {storesWithinRange?.map((store) => (
                    <ProductStockDrawerStore
                      key={store._id}
                      selectedVariant={selectedVariant}
                      store={store}
                      handleClick={handleSelectedStore}
                      selectedStore={selectedStore}
                    />
                  ))}
                </div>
              ) : null}
            </>
          </div>
        </div>
      </div>
    </div>
  );
};

export { FindInStoreDrawer };
